// Schema-served copy → the customer's language.
//
// A FormSchema's labels, help text and option labels are authored in the seed
// and served as data, so they arrive already in English and i18next never sees
// them. This resolves each one through t() KEYED ON THE FIELD KEY, with the
// served string as the defaultValue — so a locale that has caught up wins, a
// locale that has not reads the served English, and the seed stays the author of
// the source copy.
//
// It returns a NEW schema of the same shape, which is what keeps it out of the
// renderer's way: SchemaForm, the scrubber and the shared validator all keep
// operating on a FormSchemaDto and none of them learns about translation. Memoize
// on [schema, language] at the call site — the result is a fresh object.

import type { TFunction } from 'i18next';

import type { FormSchemaDto } from 'shared';

// An option's DEFINITION (the sentence the "?" panel prints beside the option
// name) and the concept name that titles it both ride in `meta`, so neither is
// reached by the field-level lookups below — untranslated, they render English
// inside an otherwise Spanish panel. Same contract as the rest of this module:
// keyed on field + option value, served string as the default. Every other meta
// key is display data the renderer computes with (applies_to, label_lines,
// popularity) and is passed through untouched.
function localizeOptionMeta(
  fieldKey: string,
  opt: FormSchemaDto['fields'][number]['enum_options'][number],
  t: TFunction,
): Record<string, unknown> | null {
  const meta = opt.meta;
  if (!meta) return null;
  const translated: Record<string, unknown> = { ...meta };
  for (const metaKey of ['help_text', 'help_label'] as const) {
    const served = meta[metaKey];
    if (typeof served === 'string' && served.trim() !== '') {
      translated[metaKey] = t(`schemaField.${fieldKey}.optionMeta.${opt.value}.${metaKey}`, {
        defaultValue: served,
      });
    }
  }
  return translated;
}

export function localizeSchemaLabels(schema: FormSchemaDto, t: TFunction): FormSchemaDto {
  return {
    ...schema,
    groups: schema.groups.map((g) => ({
      ...g,
      label: t(`schemaGroup.${g.key}.label`, { defaultValue: g.label }),
    })),
    fields: schema.fields.map((f) => ({
      ...f,
      label: t(`schemaField.${f.key}.label`, { defaultValue: f.label }),
      help_text:
        f.help_text === null
          ? null
          : t(`schemaField.${f.key}.help`, { defaultValue: f.help_text }),
      placeholder:
        f.placeholder === null
          ? null
          : t(`schemaField.${f.key}.placeholder`, { defaultValue: f.placeholder }),
      enum_options: f.enum_options.map((o) => ({
        ...o,
        label: t(`schemaField.${f.key}.option.${o.value}`, { defaultValue: o.label }),
        meta: localizeOptionMeta(f.key, o, t),
      })),
    })),
  };
}
